// Lead Filters Component

import { useState, useEffect } from 'react';
import type { LeadFilters, Customer } from '@/lib/types';
import { statusColors } from '@/lib/types';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import { debounce } from '@/lib/utils';

interface LeadFiltersComponentProps {
  filters: LeadFilters;
  onFilterChange: (filters: LeadFilters) => void;
  onClearFilters: () => void;
  customers: Customer[];
  formIdentifiers: string[];
}

export default function LeadFiltersComponent({
  filters,
  onFilterChange,
  onClearFilters,
  customers,
  formIdentifiers,
}: LeadFiltersComponentProps) {
  const [searchInput, setSearchInput] = useState(filters.search || '');

  // Debounced Search
  useEffect(() => {
    const debouncedSearch = debounce((value: string) => {
      onFilterChange({ ...filters, search: value || undefined });
    }, 300);

    debouncedSearch(searchInput);
  }, [searchInput]);

  const hasActiveFilters =
    filters.customer_slug ||
    filters.form_identifier ||
    filters.status ||
    filters.search ||
    filters.date_from ||
    filters.date_to;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Filter</h2>
        
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-gray-600"
          >
            <svg
              className="h-4 w-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Filter zurücksetzen
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Customer Filter */}
        <Select
          label="Kunde"
          value={filters.customer_slug || ''}
          onChange={(e) =>
            onFilterChange({
              ...filters,
              customer_slug: e.target.value || undefined,
            })
          }
          options={[
            { value: '', label: 'Alle Kunden' },
            ...customers.map((c) => ({
              value: c.slug,
              label: c.name,
            })),
          ]}
        />

        {/* Form Type Filter */}
        <Select
          label="Formular-Typ"
          value={filters.form_identifier || ''}
          onChange={(e) =>
            onFilterChange({
              ...filters,
              form_identifier: e.target.value || undefined,
            })
          }
          options={[
            { value: '', label: 'Alle Formulare' },
            ...formIdentifiers.map((f) => ({
              value: f,
              label: f,
            })),
          ]}
        />

        {/* Status Filter */}
        <Select
          label="Status"
          value={filters.status || ''}
          onChange={(e) =>
            onFilterChange({
              ...filters,
              status: (e.target.value as any) || undefined,
            })
          }
          options={[
            { value: '', label: 'Alle Status' },
            { value: 'new', label: statusColors.new.label },
            { value: 'contacted', label: statusColors.contacted.label },
            { value: 'in_progress', label: statusColors.in_progress.label },
            { value: 'converted', label: statusColors.converted.label },
            { value: 'archived', label: statusColors.archived.label },
          ]}
        />

        {/* Search */}
        <Input
          label="Suche"
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Name, E-Mail..."
        />
      </div>

      {/* Date Range (optional für später) */}
      {/* 
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Von Datum"
          type="date"
          value={filters.date_from || ''}
          onChange={(e) =>
            onFilterChange({
              ...filters,
              date_from: e.target.value || undefined,
            })
          }
        />
        <Input
          label="Bis Datum"
          type="date"
          value={filters.date_to || ''}
          onChange={(e) =>
            onFilterChange({
              ...filters,
              date_to: e.target.value || undefined,
            })
          }
        />
      </div>
      */}
    </div>
  );
}
